import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Check, Clock, Zap, Link2, ArrowRight, Loader, Bot, Sparkles } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { useNotificationStore } from '../store/notificationStore';
import { isPlanCancelling, planEndDate, formatPlanDate } from '../utils/planCancellation';
import { ConfirmModal } from './ui/Modal';
import { createCheckoutSession, changeSubscription, createBillingPortalSession, getPlans, createAiTokenCheckout, billingErrorMessage } from '../utils/apiClient';
import { authService } from '../services/apiService';
import { isTauri } from '../utils/tauri';
import { addPlanUpdateListener } from '../services/channelService';
import ApibooLoader from './ui/ApibooLoader';
import ContactFormModal from './ContactFormModal';
import OrderConsentStep from './plans/OrderConsentStep';
import { WithdrawContractRow } from './settings/WithdrawalSection';
import {
  CATALOG_PLAN_IDS,
  DEFAULT_PLANS,
  CURRENCIES,
  aiAllowanceLabel,
  annualPerMonth,
  effectiveCycle,
  maxSaving,
  offersAnnual,
  planSaving,
  preferredCurrency,
  priceDisplay,
  stripePriceIdForPlan,
} from '../plans/planCatalog';

async function openUrl(url) {
  if (isTauri()) {
    const { open } = await import('@tauri-apps/plugin-shell');
    await open(url);
  } else {
    window.location.href = url;
  }
}

function catalogPlans(plans) {
  return plans.filter((plan) => CATALOG_PLAN_IDS.has(plan.id));
}

function resolvePlanConfig(plans, planId) {
  return plans.find((p) => p.id === planId) || {
    id: planId,
    name: planId.replace(/_/g, ' ').toUpperCase(),
  };
}

function normalizePlan(plan) {
  return {
    id: plan.id,
    name: plan.name,
    price: plan.price,
    priceSub: plan.price_sub ?? plan.priceSub ?? '',
    description: plan.description,
    highlight: !!plan.highlight,
    aiTokensLabel: plan.ai_tokens_label ?? plan.aiTokensLabel ?? '',
    // `ai_tokens_per_month` lives here (the Apiboo AI line, aiAllowanceLabel).
    limits: plan.limits && typeof plan.limits === 'object' ? plan.limits : null,
    features: Array.isArray(plan.features) ? plan.features : [],
    stripePriceMonthly: plan.stripe_price_monthly_id ?? plan.stripePriceMonthly ?? null,
    stripePriceAnnual: plan.stripe_price_annual_id ?? plan.stripePriceAnnual ?? null,
    // Plans v2: { usd: { monthly_cents, annual_cents, label }, eur: {…} }.
    prices: plan.prices && typeof plan.prices === 'object' ? plan.prices : null,
  };
}

function formatDate(isoString) {
  if (!isoString) return '—';
  try {
    return new Date(isoString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return '—';
  }
}

/** "12 days left" / "1 day left" — or null when the date is unknown or past. */
function formatTimeLeft(isoString) {
  if (!isoString) return null;
  const end = new Date(isoString).getTime();
  if (Number.isNaN(end)) return null;
  const ms = end - Date.now();
  if (ms <= 0) return null;
  const days = Math.ceil(ms / 86400000);
  return days === 1 ? '1 day left' : `${days} days left`;
}

const AI_TOPUP_PRICE_ID = process.env.REACT_APP_STRIPE_AI_TOPUP_PRICE_ID || null;

function AiTopUpSection({ showToast }) {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(false);

  // Admins have direct grant access; hide purchase for them
  if (user?.is_admin || !AI_TOPUP_PRICE_ID) return null;

  const handleBuy = async () => {
    setLoading(true);
    try {
      const url = await createAiTokenCheckout(AI_TOPUP_PRICE_ID);
      await openUrl(url);
    } catch (err) {
      showToast?.(billingErrorMessage(err, 'Failed to start checkout. Please try again.'), 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pu-topup-section">
      <div className="pu-topup-left">
        <Sparkles size={16} className="pu-topup-icon" />
        <div>
          <p className="pu-topup-title">Need more AI credits?</p>
          <p className="pu-topup-desc">Buy a one-time pack — tokens are added on top of your monthly allowance.</p>
        </div>
      </div>
      <button
        type="button"
        onClick={handleBuy}
        disabled={loading}
        className="pu-btn pu-btn--topup"
      >
        {loading ? (
          <><Loader size={13} className="pu-btn-spinner" /> Processing…</>
        ) : (
          'Buy AI credits'
        )}
      </button>
    </div>
  );
}

function PlanCard({ plan, currentPlanId, onUpgrade, onContactSales, isRedirecting, checkoutPhase, billingCycle, currency }) {
  const { t } = useTranslation();
  const isEnterprise = plan.id === 'enterprise';
  const isCurrent = plan.id === currentPlanId;
  // Annual only where the plan has an annual price id AND amount; otherwise the
  // card stays monthly, so what it shows is what checkout charges.
  const cycle = effectiveCycle(plan, billingCycle, currency);
  const priceId = stripePriceIdForPlan(plan, cycle);
  const hasStripePrice = !!priceId;
  const isLoadingThis = isRedirecting === plan.id;

  const { price: displayPrice, sub: displaySub } = priceDisplay(plan, cycle, currency, t);
  const aiLabel = aiAllowanceLabel(plan);
  const perSeat = plan.id === 'team';
  const perMonth = cycle === 'annual' ? annualPerMonth(plan, currency) : null;
  const saving = cycle === 'annual' ? planSaving(plan, currency) : null;
  const paid = (plan.prices?.[currency]?.monthly_cents ?? 0) > 0;
  const monthlyOnly = billingCycle === 'annual' && cycle === 'monthly' && paid && !isEnterprise;

  let button;
  if (isCurrent) {
    button = (
      <button type="button" className="pu-btn pu-btn--current" disabled>
        <Check size={13} /> Current plan
      </button>
    );
  } else if (isEnterprise) {
    button = (
      <button
        type="button"
        className="pu-btn pu-btn--enterprise"
        onClick={onContactSales}
      >
        Contact sales →
      </button>
    );
  } else if (hasStripePrice) {
    button = (
      <button
        type="button"
        className="pu-btn pu-btn--upgrade"
        onClick={() => onUpgrade(plan.id)}
        disabled={!!isRedirecting}
      >
        {isLoadingThis ? (
          <>
            <Loader size={13} className="pu-btn-spinner" />
            {checkoutPhase === 'redirecting' ? 'Redirecting…' : 'Processing…'}
          </>
        ) : (
          <>Upgrade to {plan.name}</>
        )}
      </button>
    );
  } else {
    button = (
      <button type="button" disabled className="pu-btn pu-btn--soon">
        <Clock size={13} /> Coming soon
      </button>
    );
  }

  return (
    <div className={`pu-card${plan.highlight ? ' pu-card--highlight' : ''}`}>
      {plan.highlight && <span className="pu-popular">MOST POPULAR</span>}
      <span className="pu-plan-name">{plan.name}</span>
      <div className="pu-price-row" style={isEnterprise ? { flexDirection: 'column', alignItems: 'flex-start', gap: '0.2rem' } : {}}>
        <span className="pu-price">{displayPrice}</span>
        <span className="pu-price-sub">{displaySub}</span>
      </div>
      {(perMonth || saving) && (
        <div className="pu-annual-row" data-testid={`pu-annual-${plan.id}`}>
          {perMonth && (
            <span className="pu-annual-equiv">
              {t(perSeat ? 'plans.billing.perSeatMonthEquiv' : 'plans.billing.perMonthEquiv', { price: perMonth })}
            </span>
          )}
          {saving && <span className="pu-save-badge">{t('plans.billing.save', { pct: saving })}</span>}
        </div>
      )}
      {monthlyOnly && (
        <div className="pu-annual-row" data-testid={`pu-monthly-only-${plan.id}`}>
          <span className="pu-annual-equiv">{t('plans.billing.monthlyOnly')}</span>
        </div>
      )}
      <p className="pu-description">{plan.description}</p>
      <div className="pu-divider" />
      <ul className="pu-features">
        {/* The Apiboo AI allowance (owner, 16.10.2026: hosted AI and your own
            key both stay). From the catalogue's `limits.ai_tokens_per_month`
            (or a server `ai_tokens_label`); no number, no line. */}
        {aiLabel ? (
          <li className="pu-feature" data-testid={`pu-ai-${plan.id}`}>
            <span className="pu-feature-check">
              <Bot size={10} strokeWidth={2.5} />
            </span>
            {aiLabel}
          </li>
        ) : null}
        {plan.features.map((f) => (
          <li key={f} className="pu-feature">
            <span className="pu-feature-check">
              <Check size={10} strokeWidth={3} />
            </span>
            {f}
          </li>
        ))}
      </ul>
      {button}
    </div>
  );
}

function UpgradeView({ reason, onClose, showToast, plans }) {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const currentPlanId = user?.plan ?? 'free';
  const [isRedirecting, setIsRedirecting] = useState(null);
  const [checkoutPhase, setCheckoutPhase] = useState(null); // 'processing' | 'redirecting'
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [currency, setCurrency] = useState(preferredCurrency);
  const [contactFormOpen, setContactFormOpen] = useState(false);

  // When Stripe redirects back after checkout, wait for the backend to push a
  // plan_updated event via the WebSocket channel. isRedirecting holds the plan name
  // the user is upgrading TO — we close when the channel confirms that plan is active.
  useEffect(() => {
    if (!isRedirecting) return;

    const planBefore = isRedirecting;
    const remove = addPlanUpdateListener((payload) => {
      if (payload.plan !== planBefore) {
        showToast?.('Subscription activated! Welcome to your new plan.', 'success');
        setIsRedirecting(null);
        onClose();
      }
    });

    // Safety fallback: clear spinner after 30s if the channel never fires
    const timeout = setTimeout(() => setIsRedirecting(null), 30_000);

    return () => {
      remove();
      clearTimeout(timeout);
    };
  // onClose and showToast are stable refs — excluding avoids spurious re-runs.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRedirecting]);

  // The switch only appears when some plan can really be bought annually
  // (an annual price id from GET /plans and an annual amount).
  const hasAnnualPrices = plans.some((p) => offersAnnual(p, currency));
  const upTo = maxSaving(plans, currency);

  // The card the user picked, held while the order summary + consent step is
  // shown (FAGG § 8 (1), § 16 / § 18). Nothing is sent to the server before
  // the user presses the order button there.
  const [pendingOrder, setPendingOrder] = useState(null); // { planId, cycle, priceId }

  const handleUpgrade = (planId) => {
    const plan = plans.find(p => p.id === planId);
    // The same cycle the card shows: ANNUAL price id when Annual is selected
    // and the plan offers it, the monthly one otherwise.
    const cycle = plan ? effectiveCycle(plan, billingCycle, currency) : 'monthly';
    const priceId = plan ? stripePriceIdForPlan(plan, cycle) : null;
    if (!priceId) {
      if (billingCycle === 'annual') {
        showToast?.('Annual billing is not available for this plan yet.', 'info');
      } else {
        showToast?.('Stripe price ID not configured for this plan.', 'error');
      }
      return;
    }
    setPendingOrder({ planId, cycle, priceId });
  };

  const placeOrder = async ({ planId, priceId }, consent) => {
    const alreadyPaid = currentPlanId !== 'free';

    setIsRedirecting(planId);
    setCheckoutPhase('processing');
    try {
      if (alreadyPaid) {
        // Update existing subscription — avoids creating a second subscription
        const data = await changeSubscription(priceId, consent);
        if (data.success) {
          useAuthStore.setState((s) => ({
            user: { ...s.user, plan: data.plan, billing_cycle: data.billing_cycle },
          }));
          showToast?.('Plan changed successfully!', 'success');
          setIsRedirecting(null);
          setCheckoutPhase(null);
          onClose();
        } else {
          throw new Error(data.error || 'Plan change failed.');
        }
      } else {
        // New subscriber — go through Stripe Checkout
        const url = await createCheckoutSession(priceId, currency, consent);
        setCheckoutPhase('redirecting');
        await openUrl(url);
      }
    } catch (err) {
      console.error('Plan change error:', err);
      showToast?.(billingErrorMessage(err, 'Failed to change plan. Please try again.'), 'error');
      setIsRedirecting(null);
      setCheckoutPhase(null);
    }
  };

  return (
    <>
      <div className="pu-header">
        <div>
          <h2 className="pu-header-title">Upgrade your plan</h2>
          <p className="pu-header-subtitle">
            Pick a plan that scales with your team — cancel or change anytime.
          </p>
        </div>
        <button type="button" onClick={onClose} className="modal-close-btn">
          <X size={18} />
        </button>
      </div>

      {reason && (
        <div className="pu-banner">
          <Zap size={14} className="pu-banner-icon" />
          <span>{reason}</span>
        </div>
      )}

      {pendingOrder && (
        <div className="pu-scroll">
          <OrderConsentStep
            plan={plans.find((p) => p.id === pendingOrder.planId)}
            cycle={pendingOrder.cycle}
            currency={currency}
            busy={!!isRedirecting}
            phase={checkoutPhase}
            onBack={() => setPendingOrder(null)}
            onConfirm={(consent) => placeOrder(pendingOrder, consent)}
          />
        </div>
      )}

      {!pendingOrder && hasAnnualPrices && (
        <div className="pu-billing-toggle">
          <button
            type="button"
            onClick={() => setBillingCycle('monthly')}
            className={`pu-toggle-btn${billingCycle === 'monthly' ? ' pu-toggle-btn--active' : ''}`}
          >
            {t('plans.billing.monthly')}
          </button>
          <button
            type="button"
            onClick={() => setBillingCycle('annual')}
            className={`pu-toggle-btn${billingCycle === 'annual' ? ' pu-toggle-btn--active' : ''}`}
          >
            {t('plans.billing.annual')}
            {upTo && <span className="pu-save-badge">{t('plans.billing.saveUpTo', { pct: upTo })}</span>}
          </button>
        </div>
      )}

      {!pendingOrder && plans.some((p) => p.prices) && (
        <div className="pu-billing-toggle" role="group" aria-label="Currency">
          {CURRENCIES.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCurrency(c)}
              className={`pu-toggle-btn${currency === c ? ' pu-toggle-btn--active' : ''}`}
            >
              {c.toUpperCase()}
            </button>
          ))}
        </div>
      )}

      {!pendingOrder && (
      <div className="pu-scroll">
        <div className="pu-grid">
          {catalogPlans(plans).map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              currentPlanId={currentPlanId}
              onUpgrade={handleUpgrade}
              onContactSales={() => setContactFormOpen(true)}
              isRedirecting={isRedirecting}
              checkoutPhase={checkoutPhase}
              billingCycle={billingCycle}
              currency={currency}
            />
          ))}
        </div>
      </div>
      )}

      {!pendingOrder && <AiTopUpSection showToast={showToast} />}

      <div className="pu-footer">
        <span className="pu-footer-note" data-testid="pu-final-price-note">
          <span className="pu-footer-dot" />
          {/* Final prices (FAGG § 4 (1) Z 4, PrAG; legal must-do 2). The old
              line said VAT might come on top "where applicable", which tells a
              consumer the price shown may not be the price paid. Apiboo is an Austrian
              Kleinunternehmer (§ 6 (1) no. 27 UStG): no VAT is charged, so the
              card price IS the final price — say exactly that. (The currency
              is on every card now: USD and EUR, so no "Prices in EUR" either.) */}
          {t('legal.prices.finalNote')}
        </span>
        <button type="button" onClick={onClose} className="pu-footer-later" disabled={!!isRedirecting}>
          Maybe later
        </button>
      </div>

      <ContactFormModal
        isOpen={contactFormOpen}
        onClose={() => setContactFormOpen(false)}
        type="enterprise"
        lockType={true}
        showToast={showToast}
      />
    </>
  );
}

function ManageView({ user, currentPlan, onClose, onChangePlan, showToast, plans }) {
  const { t, i18n } = useTranslation();
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const { cancelPlan, reactivatePlan } = useAuthStore();

  const planConfig = resolvePlanConfig(plans, currentPlan);
  const expiresAt = user?.plan_expires_at;
  const isExpired = expiresAt && new Date(expiresAt) < new Date();
  const isEnterprise = currentPlan === 'enterprise';
  // Cancelling = `cancel_at_period_end` OR a `cancel_at` date (backend #223);
  // the plan ends on `cancel_at`, else on `plan_expires_at` (utils/planCancellation.js).
  const isCancelling = isPlanCancelling(user);
  const endsAt = isCancelling ? planEndDate(user) : expiresAt;
  const endsOnLabel = t('plans.billing.endsOn', { date: formatPlanDate(endsAt, i18n.language) });
  const timeLeft = formatTimeLeft(endsAt);
  // Admin-granted / custom plans carry no Stripe price, so there is no
  // subscription to cancel, resume, or invoice — the cancel button would just
  // 422 with "No active subscription found".
  const isStripeBacked = !!(planConfig.stripePriceMonthly || planConfig.stripePriceAnnual);

  const billingCycle = user?.billing_cycle
    ? user.billing_cycle.charAt(0).toUpperCase() + user.billing_cycle.slice(1)
    : 'Monthly';

  const seatsLabel = isEnterprise ? 'Unlimited' : 'Per seat billing';

  const activeUntilLabel = isCancelling
    ? formatPlanDate(endsAt, i18n.language)
    : isEnterprise && !expiresAt ? 'No fixed end date' : formatDate(expiresAt);

  const handleCancelConfirm = async () => {
    setCancelling(true);
    const result = await cancelPlan();
    setCancelling(false);
    setShowCancelConfirm(false);
    if (result.success) {
      // The store has the server's answer by now (cancel_at included).
      const endDate = formatPlanDate(planEndDate(useAuthStore.getState().user), i18n.language);
      useNotificationStore.getState().info(`You keep full access until ${endDate}.`, 'Subscription canceled');
    } else {
      showToast?.(result.error || 'Could not cancel your subscription. Please try again.', 'error');
    }
  };

  const handleReactivate = async () => {
    setReactivating(true);
    const result = await reactivatePlan();
    setReactivating(false);
    if (result.success) {
      showToast?.('Subscription resumed. Your plan will keep renewing as usual.', 'success');
    } else {
      showToast?.(result.error || 'Could not resume your subscription. Please try again.', 'error');
    }
  };

  const handleBillingPortal = async () => {
    setPortalLoading(true);
    try {
      const url = await createBillingPortalSession();
      await openUrl(url);
    } catch (err) {
      console.error('Billing portal error:', err);
      showToast?.(billingErrorMessage(err, 'Failed to open billing portal. Please try again.'), 'error');
    } finally {
      setPortalLoading(false);
    }
  };

  const rows = [
    { label: 'Plan', value: planConfig.name, className: 'pm-row-value--plan' },
    {
      label: 'Status',
      value: isExpired ? 'Expired' : isCancelling ? endsOnLabel : 'Active',
      className: isExpired ? 'pm-status-expired' : isCancelling ? 'pm-status-cancelling' : 'pm-status-active',
    },
    // A granted plan has no billing cycle, no renewal and no seat pricing —
    // showing "Monthly / Renews on —" for it is just noise.
    ...(isStripeBacked
      ? [
          { label: 'Billing cycle', value: billingCycle },
          { label: 'Seats included', value: seatsLabel },
          {
            label: isCancelling ? 'Full access until' : 'Renews on',
            value: isCancelling && timeLeft ? `${activeUntilLabel} (${timeLeft})` : activeUntilLabel,
          },
        ]
      : [{ label: 'Billing', value: 'Granted — no charge' }]),
  ];

  return (
    <>
      <div className="pu-header">
        <div>
          <h2 className="pu-header-title">Manage your plan</h2>
          <p className="pu-header-subtitle">
            You're on the <strong>{planConfig.name}</strong> plan.
          </p>
        </div>
        <button type="button" onClick={onClose} className="modal-close-btn">
          <X size={18} />
        </button>
      </div>

      <div className="pu-scroll pm-manage-body" style={{ position: 'relative' }}>
        {(cancelling || reactivating) && (
          <div className="pm-action-overlay">
            <ApibooLoader size={56} />
            <span className="pm-action-overlay-label">
              {cancelling ? 'Canceling subscription…' : 'Resuming subscription…'}
            </span>
          </div>
        )}

        {isCancelling && (
          <div className="pm-cancel-notice" data-testid="plan-ends-on">
            <span className="pm-cancel-notice-dot" />
            <strong>{endsOnLabel}</strong>
            {timeLeft ? ` — ${timeLeft}` : ''}. {t('plans.billing.cancelKeepAccess')}
          </div>
        )}

        <div className="pm-rows">
          {rows.map((row, i) => (
            <div key={row.label} className={`pm-row${i === rows.length - 1 ? ' pm-row--last' : ''}`}>
              <span className="pm-row-label">{row.label}</span>
              <span className={`pm-row-value${row.className ? ' ' + row.className : ''}`}>
                {row.value}
              </span>
            </div>
          ))}
        </div>

        {isStripeBacked && (
        <button
          type="button"
          className="pm-billing-link"
          onClick={handleBillingPortal}
          disabled={portalLoading}
        >
          <span className="pm-billing-link-left">
            {portalLoading ? (
              <Loader size={14} className="pu-btn-spinner" />
            ) : (
              <Link2 size={14} />
            )}
            {portalLoading ? 'Opening billing portal…' : 'Manage billing & invoices'}
          </span>
          {!portalLoading && <ArrowRight size={14} />}
        </button>
        )}

        {/* § 13a FAGG: wherever the subscription is managed, "Withdraw from
            contract" is there too (see settings/WithdrawalSection.js). */}
        {isStripeBacked && (
          <WithdrawContractRow
            planName={planConfig.name}
            billingCycle={user?.billing_cycle}
            email={user?.email}
            startedAt={user?.subscription_started_at || user?.plan_started_at || null}
          />
        )}

        <AiTopUpSection showToast={showToast} />
      </div>

      <div className="pu-footer">
        {!isStripeBacked ? (
          <span className="pm-cancelled-label">Granted plan — no billing attached</span>
        ) : isCancelling ? (
          <span className="pm-cancelled-label">
            {endsOnLabel}{timeLeft ? ` · ${timeLeft}` : ''}
          </span>
        ) : (
          <button
            type="button"
            onClick={() => setShowCancelConfirm(true)}
            className="pm-cancel-btn"
            disabled={cancelling}
          >
            {cancelling ? 'Canceling…' : 'Cancel subscription'}
          </button>
        )}
        <div className="pm-footer-actions">
          {isCancelling && isStripeBacked ? (
            <button
              type="button"
              onClick={handleReactivate}
              className="pm-reactivate-btn"
              disabled={reactivating}
            >
              {reactivating ? <><Loader size={13} className="pu-btn-spinner" /> Resuming…</> : 'Resume subscription'}
            </button>
          ) : (
            <button type="button" onClick={onChangePlan} className="pm-change-btn">
              Change plan
            </button>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={handleCancelConfirm}
        title="Cancel your subscription?"
        message={`Your ${planConfig.name} plan stays fully active until ${formatDate(expiresAt)}${timeLeft ? ` (${timeLeft})` : ''}. You won't be charged again, and after that date your account moves to the free plan. You can resume any time before then.`}
        confirmText="Cancel subscription"
        cancelText="Keep my plan"
        type="danger"
      />
    </>
  );
}

export default function PlanUpgradeModal({ isOpen, onClose, reason, showToast, forceUpgrade }) {
  const { user } = useAuthStore();
  const currentPlan = user?.plan ?? 'free';
  const isPaid = currentPlan !== 'free';
  const [changingPlan, setChangingPlan] = useState(false);
  const [plans, setPlans] = useState(DEFAULT_PLANS);

  // Re-fetch user + plan catalog every time the modal opens.
  useEffect(() => {
    if (!isOpen) { setChangingPlan(false); return; }

    authService.getUser()
      .then((fresh) => useAuthStore.setState({ user: fresh }))
      .catch(() => {});

    getPlans()
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          // Keep custom/admin-granted plans in the list so ManageView can
          // resolve the user's actual plan name; the upgrade grid still
          // filters down to the catalog four at render time.
          setPlans(data.map(normalizePlan));
        }
      })
      .catch(() => setPlans(DEFAULT_PLANS));
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === 'Escape') {
        e.stopImmediatePropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', handler, true);
    return () => document.removeEventListener('keydown', handler, true);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const showManage = isPaid && !changingPlan && !forceUpgrade;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="modal-backdrop" onClick={onClose} />
      <div
        className="relative mx-4 w-full modal-content flex flex-col pu-modal"
        style={{ maxWidth: showManage ? 620 : 980 }}
      >
        {showManage ? (
          <ManageView
            user={user}
            currentPlan={currentPlan}
            onClose={onClose}
            onChangePlan={() => setChangingPlan(true)}
            showToast={showToast}
            plans={plans}
          />
        ) : (
          <UpgradeView reason={reason} onClose={onClose} showToast={showToast} plans={plans} />
        )}
      </div>
    </div>
  );
}
